import React, { useState } from 'react';
import { FaDownload, FaFileExcel, FaImage } from 'react-icons/fa';
import {
  captureElementAsPng,
  copyBlobToClipboard,
  downloadBlob,
  downloadElementAsPdf
} from './reportExport';

const ReportSectionActions = ({
  sectionRef,
  fileBase,
  onExportExcel,
  onExportPdf,
  disabled = false,
  captureClassName = '',
  pdfOptions = null
}) => {
  const [copyingPng, setCopyingPng] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [error, setError] = useState('');

  const waitForNextFrame = () => new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });

  const handleCopyPng = async () => {
    const element = sectionRef?.current;
    if (!element) {
      setError('Section is not available');
      return;
    }

    setCopyingPng(true);
    setError('');

    try {
      if (captureClassName) {
        element.classList.add(captureClassName);
        await waitForNextFrame();
      }

      const { blob } = await captureElementAsPng(element);
      const filename = `${fileBase}.png`;
      try {
        const copied = await copyBlobToClipboard(blob, filename);
        if (!copied) {
          downloadBlob(blob, filename);
        }
      } catch {
        downloadBlob(blob, filename);
      }
    } catch (err) {
      setError(err.message || 'Failed to copy section as PNG');
    } finally {
      if (captureClassName) {
        element.classList.remove(captureClassName);
      }
      setCopyingPng(false);
    }
  };

  const handleExportPdf = async () => {
    if (!pdfOptions) {
      onExportPdf?.();
      return;
    }

    const element = sectionRef?.current;
    if (!element) {
      setError('Section is not available');
      return;
    }

    setExportingPdf(true);
    setError('');

    try {
      await downloadElementAsPdf(element, {
        filename: `${fileBase}.pdf`,
        captureClassName,
        ...pdfOptions
      });
    } catch (err) {
      setError(err.message || 'Failed to export section as PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const actionButtonClass = 'inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-orange-300 hover:text-orange-700 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" onClick={handleCopyPng} className={actionButtonClass} disabled={disabled || copyingPng}>
        <FaImage />
        {copyingPng ? 'Copying' : 'PNG'}
      </button>
      <button type="button" onClick={onExportExcel} className={actionButtonClass} disabled={disabled}>
        <FaFileExcel />
        Excel
      </button>
      <button type="button" onClick={handleExportPdf} className={actionButtonClass} disabled={disabled || exportingPdf}>
        <FaDownload />
        {exportingPdf ? 'Preparing' : 'PDF'}
      </button>
      {error ? <span className="text-xs text-rose-600">{error}</span> : null}
    </div>
  );
};

export default ReportSectionActions;
